import SortOrders from "./sort-orders";
import ServiceUtils from "./service-utils";
import Parameter from "./parameter";
import MigrationMapping from "./migration-mapping";
import Field from "./field";
import Entity from "./entity";
import Documents from "./documents";
import DocumentValue from "./document-value";
import { parsePathId, sortByOrder } from "../utils";
import ISubstitution from "../interface/substitution";
import IPublicTableOwner from "../interface/public-table-owner";
import IPublicTable from "../interface/public-table";
import IField from "../interface/field";
import IEntity from "../interface/entity";
import IDocumentValue from "../interface/document-value";
import IDocument from "../interface/document";
import ServiceUtil from "../enum/service-util";
import ParamStorage from "../enum/param-storage";
import { PublicTableTypes, ValuableTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";
import ErrorCode from "../enum/error-code";
import ParameterStorage from "../class/parameter-storage";

class PublicTableSingleton {

    private readonly Store = ParamStorage.PublicTables;

    public getAll(entityId: number | null = null): IPublicTable[] {
        const all: IPublicTable[] = this.getStore().getAll();
        if (entityId) {
            const order: number[] = SortOrders.get(this.Store, entityId);
            const result: IPublicTable[] = all.filter(a => a.entityId === entityId).map(a => this.toClient(a));
            return sortByOrder(result, 'id', order);
        }
        return all.map((a: IPublicTable) => this.toClient(a));
    }

    public get(id: number): IPublicTable | null {
        const table: IPublicTable | null = this.getStore().get(id);
        return table ? this.toClient(table) : null;
    }

    public getRaw(id: number): IPublicTable | null {
        return this.getStore().get(id);
    }

    public async create(table: IPublicTable): Promise<IPublicTable> {
        return this.getStore().create(this.toRaw(table));
    }

    public async update(table: IPublicTable): Promise<IPublicTable> {
        return this.getStore().update(this.toRaw(table));
    }

    public async delete(id: number): Promise<number> {
        return this.getStore().delete(id);
    }

    public getByOwner(pathId: number[]): IPublicTable[] {
        const field: IField | null = Field.get(pathId.slice(-1)[0]);
        if (field) {
            return this.getAll().filter((table: IPublicTable) => {
                const owner: IField | null = Field.get(table.ownerFieldId);
                if (owner) {
                    if (owner.uiType === FieldType.pointer) {
                        return owner.pointer[1].ptEcn === field.entityId;
                    } else {
                        return owner.id === field.id;
                    }
                }
                return false;
            });
        }
        return [];
    }

    public getFields(entityId: number, ownerId: number): IField[] {
        const entity: IEntity | null = Entity.get(entityId);
        if (entity) {
            return entity.fields.filter((f: IField) => {
                if (PublicTableTypes.includes(f.uiType)) {
                    if (f.uiType === FieldType.linked) {
                        return f.linkedRelation[0] !== ownerId;
                    }
                    return f.id !== ownerId;
                }
                return false;
            });
        }
        return [];
    }

    public getOwners(entityId: number): IPublicTableOwner[] {
        const result: IPublicTableOwner[] = [];
        Entity.getFieldsByType(entityId, [FieldType.linked]).forEach((f: IField) => {
            result.push({
                id: f.linkedRelation[0],
                name: f.singular
            });
        });
        return result;
    }

    public getSubstitutions(entityId: number, ownerId: number): ISubstitution[] {
        return ServiceUtils.substitutions(entityId, ValuableTypes, 1, false).filter(sub => {
            return sub.pathId[0] === ownerId;
        });
    }

    public getVisualName(tableId: number, doc: IDocument): string {
        const table: IPublicTable | null = this.getRaw(tableId);
        if (table) {
            return ServiceUtils.replaceEntries(table.name, ServiceUtil.Sub, (params: (string | number)[]) => {
                const fieldId: number | undefined = parsePathId(params[0].toString()).pop();
                if (fieldId !== undefined) {
                    return DocumentValue.getByPathSync(doc, [fieldId]);
                }
            });
        }
        return '';
    }

    public async loadData(tableId: number, ownerId: number): Promise<IDocument[]> {
        const table: IPublicTable | null = this.getRaw(tableId);
        if (table) {
            const entity: IEntity | null = Entity.get(table.entityId);
            if (entity) {
                const docs: IDocument[] = await Documents.loadDocuments(entity.id, {
                    filters: [{ fieldId: table.ownerFieldId, value: [ownerId] }]
                }, false);
                await Documents.loadRelatedDocs(entity, docs);
                Documents.setLinkedFieldValues(entity, docs);
                return docs;
            } else {
                throw ErrorCode.NoEntity;
            }
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    public async getByMapping(oldId: string): Promise<IPublicTable | null> {
        const newId: number | null = await MigrationMapping.get(this.Store, oldId);
        if (newId !== null) {
            return this.get(newId);
        }
        return null;
    }

    public async setOrder(entityId: number | null = null, order: number[]): Promise<IPublicTable[]> {
        await SortOrders.set({ param: this.Store, entityId, order });
        return this.getAll(entityId);
    }

    /* Private Public Methods */

    public getOwnerId(tableId: number, entityId: number, doc: IDocument): number | null {
        const table: IPublicTable | null = this.get(tableId);
        if (table) {
            const owner: IField | null = Field.get(table.ownerFieldId);
            if (owner) {
                const ownerEntityId: number = owner.pointer[1].ptEcn;
                if (ownerEntityId === entityId) {
                    return doc.id as number;
                } else {
                    const value: IDocumentValue = DocumentValue.getByPathSync(doc, [owner.id]);
                    const pointerValue: number[] = DocumentValue.getLinked(value);
                    if (pointerValue.length) {
                        return pointerValue[0];
                    }
                }
            }
        }
        return null;
    }

    /* Private Methods - Parameter Storage */

    private toClient(table: IPublicTable): IPublicTable {
        table.name = ServiceUtils.replaceSubsToName(table.entityId, table.name);
        return table;
    }

    private toRaw(table: IPublicTable): IPublicTable {
        table.name = ServiceUtils.replaceSubsToId(table.entityId, table.name);
        return table;
    }

    private getStore(): ParameterStorage<IPublicTable> {
        return Parameter.get(this.Store);
    }

}

const PublicTable = new PublicTableSingleton();

export default PublicTable;